import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { fetchSetting } from "@/lib/api";
import { useContent } from "@/lib/ContentContext";
import { ClickableImage } from "@/components/Lightbox";

/*
 * Homepage Giving section, consolidated per Delivery 6 parish feedback:
 *   - Tithe & Offertory poster
 *   - Project Annex poster
 *   - Mass Intentions copy + generosity CTA + Parish Office phone(s)
 *
 * The Paybill number lives on both posters, so the standalone Paybill text
 * card was removed to avoid duplicating the same number twice.
 */
const FALLBACKS = {
    tithe: "/images/posters/offertory.png",
    projectAnnex: "/images/posters/project-annex.png",
};

// PaybillCard remains exported for any future page that needs a standalone card.
// It is intentionally NOT rendered on the homepage giving section.
export const PaybillCard = ({ heading, paybillNumber, accountLabelTithe, accountLabelOthers }) => (
    <div
        data-testid="church-paybill-card"
        className="rounded-2xl overflow-hidden shadow-xl flex flex-col"
        style={{ background: "#383838", border: "1px solid rgba(212, 175, 55, 0.55)" }}
    >
        <div className="px-6 py-3" style={{ background: "linear-gradient(90deg, #7A1F2B, #900000, #7A1F2B)", borderBottom: "1px solid rgba(212, 175, 55, 0.4)" }}>
            <h3 className="text-xl uppercase tracking-[0.18em] text-center" style={{ color: "#D4AF37" }}>{heading}</h3>
        </div>
        <div className="p-6 flex-1 flex flex-col justify-center space-y-6">
            <div className="text-center">
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-1" style={{ color: "#D4AF37" }}>Paybill Number</p>
                <p data-testid="paybill-number" className="text-4xl sm:text-5xl font-bold tabular-nums" style={{ color: "#F3EDE4" }}>{paybillNumber}</p>
            </div>
            <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-3 text-center" style={{ color: "#D4AF37" }}>Account Number</p>
                <ul className="space-y-2 text-sm sm:text-base" style={{ color: "#F3EDE4" }}>
                    <li className="flex items-start gap-2"><span style={{ color: "#900000" }}>●</span><span>{accountLabelTithe}</span></li>
                    <li className="flex items-start gap-2"><span style={{ color: "#900000" }}>●</span><span>{accountLabelOthers}</span></li>
                </ul>
            </div>
        </div>
    </div>
);

const PosterPanel = ({ src, alt, testId, fallback }) => (
    <div data-testid={testId} className="rounded-2xl overflow-hidden border border-gold-300/40 shadow-xl bg-cream-50">
        <ClickableImage
            src={src}
            alt={alt}
            className="w-full h-auto block"
            onError={(e) => { if (e.currentTarget.src !== window.location.origin + fallback) e.currentTarget.src = fallback; }}
        />
    </div>
);

const TitheOffertory = () => {
    const { content } = useContent();
    const MI = content.intentions;
    const C = content.contact;
    const [titheSrc, setTitheSrc] = useState(FALLBACKS.tithe);
    const [annexSrc, setAnnexSrc] = useState(FALLBACKS.projectAnnex);

    useEffect(() => {
        let alive = true;
        fetchSetting("tithe_poster").then((d) => { if (alive && d?.value && d?.url) setTitheSrc(d.url); }).catch(() => {});
        fetchSetting("project_annex").then((d) => { if (alive && d?.value && d?.url) setAnnexSrc(d.url); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    return (
        <section
            data-testid="home-giving-section"
            className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-16"
        >
            {/* Section title + description STACKED (description under the heading) */}
            <div className="mb-6">
                <span className="eyebrow">Give</span>
                <h2 className="heading-section mt-1">Tithe, Offertory &amp; Project Annex</h2>
                <p className="mt-3 max-w-3xl text-sm sm:text-base text-maroon-700/85 leading-relaxed">
                    Support the mission of Holy Trinity Kileleshwa through Mobile Money.
                </p>
            </div>

            {/* Two posters, side-by-side on desktop */}
            <div className="grid gap-6 md:grid-cols-2">
                <PosterPanel
                    src={titheSrc}
                    alt="Tithe and Offertory poster, Holy Trinity Kileleshwa"
                    testId="home-tithe-offertory"
                    fallback={FALLBACKS.tithe}
                />
                <PosterPanel
                    src={annexSrc}
                    alt="Project Annex poster, Holy Trinity Kileleshwa"
                    testId="home-project-annex"
                    fallback={FALLBACKS.projectAnnex}
                />
            </div>

            {/* Mass Intentions integrated into Giving */}
            <div
                data-testid="mass-intentions-block"
                className="mt-8 rounded-2xl border border-gold-300/50 bg-cream-50 p-6 shadow-sm"
            >
                <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-gold-400" />
                    <h3 className="text-xl text-maroon-800">{MI.heading}</h3>
                </div>
                <div className="gold-divider my-3 opacity-50" />
                <p className="text-sm sm:text-base text-maroon-700/90 leading-relaxed">
                    {MI.body}
                </p>

                {/* Generosity call-to-action, dark card + white text for proper contrast */}
                <div
                    data-testid="giving-cta-card"
                    className="mt-5 rounded-xl p-5"
                    style={{
                        background: "linear-gradient(135deg, #7A1F2B 0%, #900000 100%)",
                        border: "1px solid rgba(212, 175, 55, 0.55)",
                        boxShadow: "0 6px 18px rgba(0, 0, 0, 0.25)",
                    }}
                >
                    <p
                        data-testid="giving-cta-copy"
                        className="text-sm sm:text-base italic leading-relaxed"
                        style={{ color: "#FFFFFF" }}
                    >
                        As you lay your petitions before God, we invite you to
                        <span style={{ color: "#D4AF37" }}> support the ongoing work of His house</span>.
                        Your generous contribution helps keep our sanctuary open, our ministries thriving,
                        and our community served.
                    </p>
                </div>

                {/* Parish Office phone numbers (up to two) */}
                <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 text-sm">
                    <span className="text-xs font-semibold uppercase tracking-widest text-maroon-700/70">
                        Call the Parish Office
                    </span>
                    {C.phone && (
                        <a href={`tel:${(C.phone || "").replace(/\s/g, "")}`} data-testid="mass-intention-call" className="btn-ghost">
                            <Phone size={14} /> {C.phone}
                        </a>
                    )}
                    {C.phone2 && (
                        <a href={`tel:${(C.phone2 || "").replace(/\s/g, "")}`} data-testid="mass-intention-call-2" className="btn-ghost">
                            <Phone size={14} /> {C.phone2}
                        </a>
                    )}
                </div>
            </div>
        </section>
    );
};

export default TitheOffertory;
